// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoCard from "@mcmc/App/Widgets/InfoCard/InfoCard.component";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface SolutionsListStyles {
    solutionsWrapper?: InjectableCss;
    solutionsTitle?: TypographyProps;
    solutionsSummary?: TypographyProps;
    solutionsGridContainer?: GridContainerProps;
    solutionsGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const solutionsListStyles: SolutionsListStyles = {
    solutionsWrapper: {
        css: css`
            margin-top: 42px;
        `,
    },
    solutionsTitle: {
        variant: "h1",
        size: 32,
        css: css`
            line-height: 38px;
            letter-spacing: 0.43px;
        `,
    },
    solutionsSummary: {
        as: "p",
        size: 18,
        css: css`
            line-height: 25px;
            letter-spacing: 0;
            white-space: pre-line;
            margin-bottom: 45px;
        `,
    },
    solutionsGridContainer: {
        gap: 30,
    },
    solutionsGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            @media print {
                width: 100%;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = solutionsListStyles;
const mapStateToProps = (state: ApplicationState) => {
    const currentPage = getCurrentPage(state);
    const parentNodeId = currentPage.nodeId;
    const pages = getDataView(state.data.pages, { parentNodeId });
    const pageLink = getPageLinkByNodeId(state, currentPage.nodeId)?.url;
    return {
        currentPage,
        parentNodeId,
        pages,
        pageLink,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SolutionsAndServicesWidget: React.FC<Props> = ({
    currentPage,
    parentNodeId,
    pages,
    loadPagesForParent,
    pageLink,
}) => {
    const { value: pagesValue, isLoading } = pages;
    const { title, summary } = currentPage.fields;
    useEffect(() => {
        if (!pagesValue && !isLoading) {
            loadPagesForParent({ parentNodeId });
        }
    }, []);

    return (
        <StyledWrapper {...styles.solutionsWrapper}>
            <Typography {...styles.solutionsTitle}>{title}</Typography>
            {summary && <Typography {...styles.solutionsSummary}>{summary}</Typography>}
            {isLoading ? (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            ) : (
                <GridContainer {...styles.solutionsGridContainer}>
                    {pagesValue &&
                        pagesValue.map(page => {
                            const { summary, thumbnail, title, urlSegment } = page.fields;
                            return (
                                <GridItem key={page.id} {...styles.solutionsGridItem}>
                                    <InfoCard
                                        imageUrl={thumbnail}
                                        imageCaption={title}
                                        copy={summary}
                                        url={`${pageLink}/${urlSegment}`}
                                        linkAlignment="left"
                                        linkText={translate("Learn More")}
                                        direction="horizontal"
                                        openInNewTab={false}
                                        maxNumberOfCopyRows={4}
                                        isSolutionsWidget={false}
                                    />
                                </GridItem>
                            );
                        })}
                </GridContainer>
            )}
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Common",
    allowedContexts: ["SolutionsAndServicesPage" as any],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SolutionsAndServicesWidget),
    definition,
};

export default widgetModule;
